/**
 * Issue Tickets API Service
 *
 * Service functions for interacting with the issue tickets API endpoints.
 */

import { apiClient } from "./api";
import type { IssueTicket } from "@/types/issueTicket";

const API_PREFIX = "/api/v1/security-issues";

/**
 * Paginated response from backend
 */
export interface PaginatedTicketsResponse {
  data: IssueTicket[];
  total_count: number;
  has_more: boolean;
  page: number;
  items_per_page: number;
}

/**
 * Request body for creating a ticket
 */
export interface CreateTicketData {
  provider?: string;
  project_key: string;
  issue_type?: string;
  summary?: string;
  description?: string;
}

/**
 * List tickets for a security issue.
 * @param issueId - The numeric ID of the issue
 * @param page - The page number (default: 1)
 */
export async function listIssueTickets(
  issueId: number,
  page: number = 1
): Promise<PaginatedTicketsResponse> {
  const params = new URLSearchParams();
  params.append("page", page.toString());
  return apiClient.get<PaginatedTicketsResponse>(
    `${API_PREFIX}/${issueId}/tickets?${params.toString()}`
  );
}

/**
 * Create a Jira ticket for a security issue.
 * @param issueId - The numeric ID of the issue
 * @param data - The ticket creation data
 */
export async function createIssueTicket(
  issueId: number,
  data: CreateTicketData
): Promise<IssueTicket> {
  return apiClient.post<IssueTicket>(`${API_PREFIX}/${issueId}/tickets`, data);
}

/**
 * Sync (refresh) a ticket's external status from Jira.
 * @param ticketId - The numeric ID of the ticket record
 */
export async function syncIssueTicket(ticketId: number): Promise<IssueTicket> {
  return apiClient.post<IssueTicket>(`${API_PREFIX}/tickets/${ticketId}/sync`);
}
